import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEarlyWarningsFilters } from '../../../hooks/useEarlyWarningsFilters';
import { FilterConfigValues, IconCode, TabFilterOption } from '../../../models/EarlyWarnings';
import { getProjectsSimpleTableIndicator } from '../../../redux/actions/indicatorProjects';
import { CLASSIFICATION_OF_ACQUISITION_PLANS } from '../../../services/sessionFiltersIndicatorDetailHelper';
import { EarlyWarningsFilters as ClassificationOfAcquisitionPlansEarlyWarningsFilters } from '../EarlyWarningsFilters/EarlyWarningsFilters';
import ClassificationOfAcquisitionPlansTable from './Table/Table';
import { createFilterConfigComponentPlans } from '../EarlyWarningsFilters/Util';
import { handleLoading } from '../../utils';
import './ClassificationOfAcquisitionPlans.scss';
import { ReactComponent as IconAlertMulyple } from '../../../assets/icons/u_alert-multyple.svg';
import { ReactComponent as IconAlertExclamation } from '../../../assets/icons/u_alert-exclamacion.svg';
import { ReactComponent as IconAlertCheck } from '../../../assets/icons/u_alert-check.svg';
import { useTranslation } from 'react-i18next';
import { setIndicatorResponse, setLoading } from '../../../redux/actions/dataTable';
import { getNewFilteredIndicators } from '../../../api/indicatorApi';
import { translateMultipleText } from '../../../services/stringUtil';
import { emptyProjects } from '../../Portfolio/PortfolioUtil';
import { processIndicators } from '../../Portfolio/indicatorHelper';
import { ProjectProductTypes } from '../../../models/ProjectProductTypes';
import { arrayMove } from 'react-sortable-hoc';
import { addIndicators } from '../../../redux/actions/indicators';
import { setCurrentIndicator } from '../../../redux/actions/currentIndicator';
import { resetIndicatorDetailFilter } from '../../../redux/actions/indicatorDetailFilters';
import { updateCreatingOrEditing } from '../../../redux/actions/menuTabs';
import Filter from '../../Portfolio/Filter/Filter';
import FilterIndicators from '../../../models/FilterIndicators';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import ErrorMessageView from '../../Util/ErrorMessage/ErrorMessageView';

const filterConfigValues: FilterConfigValues[] = [
  { id: 0, filterIcon: IconCode.GREEN, filterLabel: 'satisfactory' },
  { id: 1, filterIcon: IconCode.YELLOW, filterLabel: 'last_alert' },
  { id: 2, filterIcon: IconCode.RED, filterLabel: 'filter_problem' },
];
const filterConfigComponent: TabFilterOption[] = createFilterConfigComponentPlans(filterConfigValues);

const defaultSessionFilterObject = {
  filterConfigState: filterConfigComponent,
  changeProjectType: true,
};

export const ClassificationOfAcquisitionPlans = () => {
  const dispatch = useDispatch();
  const { currentIndicator } = useSelector<any, any>(state => state);
  const { filterConfigState, handleEarlyWarningsFilter, handleProjectsQuantities } = useEarlyWarningsFilters({
    sessionKey: CLASSIFICATION_OF_ACQUISITION_PLANS,
    defaultSessionFilterObject,
  });
  const [loadingData, setLoadingData] = useState<boolean>(false);

  const { t } = useTranslation();

  // Filter
  const filterIndicators = useSelector<any, FilterIndicators>(state => state.filterIndicators);
  const stateProjectProductTypes = useSelector<any, FilterIndicators>(
    state => state.indicatorPayload.payload.projectProductTypes,
  );
  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const portfolioSelectedTab = menuTabs.portfolioSelectedTab;
  const creatingOrEditing = menuTabs.creatingOrEditing;
  const [isLoading, setIsLoading] = useState(false);
  const [totalProjectsData, setTotalProjectsData] = useState(false);
  const [flag, setFlag] = useState<boolean>(false);
  const [hasError, setHasError] = useState(false)

  const projectTypeFilterChange = {
    ...filterIndicators,
    common: { ...filterIndicators.common, projectTypeFilters: stateProjectProductTypes },
    projectTypeFilters: stateProjectProductTypes,
  };

  const handleCommonFilterChange = async () => {
    dispatch(setLoading(true));
    setIsLoading(true);
    setHasError(false);

    try {
      const indicatorsToProcess = await getNewFilteredIndicators(
        {},
        { projectProductTypes: stateProjectProductTypes },
        {
          ...projectTypeFilterChange.common,
        },
      );
      dispatch(setIndicatorResponse(indicatorsToProcess));
      startProcessIndicators(
        indicatorsToProcess,
        translateMultipleText(` ${' '} ${portfolioSelectedTab.title}`, t),
        filterIndicators.common.projectTypeFilters,
      );
      setTotalProjectsData(emptyProjects(indicatorsToProcess.totalProjects));
    } catch (err) {
      console.error(err, 'Error fetching data from indicators');
      setHasError(true);
    } finally {
      dispatch(setLoading(false));
      setIsLoading(false);
    }
  };

  const startProcessIndicators = (inds, titleFromTab, projectTypeFilter = [ProjectProductTypes.Loan]) => {
    const result = processIndicators(inds, {
      titleFromTab,
      projectTypeFilter,
    });
    const elementSummary: any = result.find(elem => elem.id === 15);
    const indexSummary: number = result.indexOf(elementSummary);
    const sendEndSummary = arrayMove(result, indexSummary, result.length);

    dispatch(addIndicators(sendEndSummary));
    dispatch(
      setCurrentIndicator(sendEndSummary?.find(data => data.indicatorKey === 'CLASSIFICATION_OF_ACQUISITION_PLANS')),
    );
    setIsLoading(false);
  };

  // const tabId0OrCreatingEditing = (menuTabs, creatingOrEditing) =>
  //   menuTabs.portfolioSelectedTab.tabId === 0 || creatingOrEditing;

  useEffect(() => {
    getFilteredSimpleIndicators(
      dispatch,
      currentIndicator.id,
      currentIndicator.indicatorKey,
      setLoadingData,
      projectTypeFilterChange,
      setHasError,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndicator.id, dispatch, currentIndicator]);

  useEffect(() => {
    if (
      filterIndicators.common.coReponsibleDivisions.length > 0 ||
      filterIndicators.common.countryId.length > 0 ||
      filterIndicators.common.divisionId.length > 0 ||
      filterIndicators.common.interestIssues.length > 0 ||
      filterIndicators.common.participantDivisions.length > 0 ||
      filterIndicators.common.regionId.length > 0 ||
      filterIndicators.common.sectors.length > 0 ||
      filterIndicators.common.teamLeaders.length > 0
    )
      handleCommonFilterChange();
    setFlag(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  useEffect(() => {
    dispatch(resetIndicatorDetailFilter());
    if (
      !menuTabs.firstLoad &&
      // tabId0OrCreatingEditing(menuTabs, creatingOrEditing) &&
      filterIndicators.common.coReponsibleDivisions.length === 0 &&
      filterIndicators.common.countryId.length === 0 &&
      filterIndicators.common.divisionId.length === 0 &&
      filterIndicators.common.interestIssues.length === 0 &&
      filterIndicators.common.participantDivisions.length === 0 &&
      filterIndicators.common.regionId.length === 0 &&
      filterIndicators.common.sectors.length === 0 &&
      filterIndicators.common.teamLeaders.length === 0 &&
      flag
    ) {
      handleCommonFilterChange();
      setFlag(false);
      if (creatingOrEditing) {
        dispatch(updateCreatingOrEditing(false));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  useEffect(() => {
    if (!sessionStorage.getItem(CLASSIFICATION_OF_ACQUISITION_PLANS)) {
      sessionStorage.setItem(CLASSIFICATION_OF_ACQUISITION_PLANS, JSON.stringify(defaultSessionFilterObject));
    }
  }, []);

  const renderIcon = calification => {
    if (calification < 0.36) {
      return <IconAlertMulyple />;
    } else if (calification > 0.35 && calification <= 0.7) {
      return <IconAlertExclamation />;
    } else {
      return <IconAlertCheck />;
    }
  };
  return (
    <div>
      {!hasError ? (
        <>
          <div className="filters-detail">
            <ClassificationOfAcquisitionPlansEarlyWarningsFilters
              filterSet={filterConfigState}
              handleFilter={handleEarlyWarningsFilter}
            />
          </div>
          <div className="head-clasification">
            <div className="clasification-text">
              <p>{t('new_portfolio_rating')}</p>
              <h4>
                {renderIcon(currentIndicator.detailExtraProps.totalWeight)} {currentIndicator.detailExtraProps.totalWeight}
              </h4>
            </div>
          </div>
          <div>
            <ClassificationOfAcquisitionPlansTable
              projectsQuantities={handleProjectsQuantities}
              earlyWarningsTab={[
                filterConfigState[0].selected,
                filterConfigState[1].selected,
                filterConfigState[2].selected,
              ]}
              loading={loadingData}
            />
          </div>
        </>
      ) : (
        <div className="ErrorMessageStyle">
          <ErrorMessageView
            title={t('title_error_message_view')}
            description={t('desc_error_message_tab')}
            reload={handleCommonFilterChange}
            isIndicator={true}
          />
        </div>
      )}

    </div>
  );
};

const getFilteredSimpleIndicators = (
  d,
  currentIndicatorId,
  currentIndicatorKey,
  setLoading,
  projectTypeFilterChange?,
  setHasError?,
) => {
  try {
    setHasError?.(false);
    return handleLoading(
      () => d(getProjectsSimpleTableIndicator(currentIndicatorId, currentIndicatorKey, projectTypeFilterChange)),
      setLoading,
    );
  } catch (error) {
    console.error(error, 'Error fetching filtered simple indicators');
    setHasError?.(true);
  }
};
